'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Check, RotateCcw, Shield, Sparkles, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/user-context';
import { useOfferGate } from '@/hooks/use-offer-gate';
import { useSubscriptionManager } from '@/hooks/use-subscription-manager';
import { useTrackingEvents } from '@/hooks/use-tracking-events';

type PlanId = 'cosmic-navigator' | 'cosmic-oracle';
type PaywallStage = 'hard' | 'downsell';
type OfferContext = 'default' | 'compatibility' | 'tarot' | 'money';

const PRIMARY_PLAN: PlanId = 'cosmic-navigator';

const STAGE_BILLING_CYCLE: Record<PaywallStage, 'monthly' | 'annual'> = {
  hard: 'annual',
  downsell: 'monthly',
};

type OfferGateOverlayProps = {
  context?: OfferContext;
  preview?: boolean;
};

export function OfferGateOverlay({
  context = 'default',
  preview = false,
}: OfferGateOverlayProps = {}) {
  const t = useTranslations('pages.offer.gate');
  const { userProgress } = useUser();
  const { shouldShowOffer, resolveOffer, state } = useOfferGate(userProgress);
  const { createCheckoutSession, isCreatingCheckout } = useSubscriptionManager();
  const [stage, setStage] = useState<PaywallStage>('hard');
  const [isPreviewDismissed, setIsPreviewDismissed] = useState(false);
  const tracking = useTrackingEvents();
  const paywallVariant = userProgress?.paywallVariant ?? 'soft';
  const isHardVariant = paywallVariant === 'hard';
  const trackedViewKeyRef = useRef<string | null>(null);
  const isDevelopmentPreview = preview && process.env.NODE_ENV === 'development';
  const isVisible = shouldShowOffer || (isDevelopmentPreview && !isPreviewDismissed);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const viewKey = state?.createdAt ?? 'unknown';
    if (trackedViewKeyRef.current === viewKey) {
      return;
    }
    tracking.trackPaywallView({
      surface: 'offer_gate_modal',
      reason: state?.reason ?? null,
      paywall_variant: paywallVariant,
      offer_context: context,
      stage,
    });
    trackedViewKeyRef.current = viewKey;
  }, [context, isVisible, paywallVariant, stage, state?.createdAt, state?.reason, tracking]);

  if (!isVisible) {
    return null;
  }

  const billingCycle = STAGE_BILLING_CYCLE[stage];
  const contextKey = context ?? 'default';
  const benefits = [0, 1, 2].map((index) => t(`contexts.${contextKey}.benefits.${index}`));

  const handlePrimary = () => {
    const plan = PRIMARY_PLAN;
    if (stage === 'hard') {
      tracking.trackOfferTrialStart({
        plan,
        reason: state?.reason ?? 'signup',
        source: 'offer_gate_modal',
        paywall_variant: paywallVariant,
      });
    } else {
      tracking.trackCheckoutStart({
        plan,
        billing_cycle: billingCycle,
        source: 'offer_gate_modal',
      });
    }
    resolveOffer('accepted');
    createCheckoutSession(plan, billingCycle);
  };

  const handleSkip = () => {
    if (isDevelopmentPreview) {
      setIsPreviewDismissed(true);
      return;
    }
    if (isHardVariant) {
      return;
    }
    tracking.trackOfferSkip({
      plan: PRIMARY_PLAN,
      reason: state?.reason ?? 'signup',
      source: 'offer_gate_modal',
      paywall_variant: paywallVariant,
    });
    resolveOffer('skipped');
  };

  const handleClose = () => {
    if (stage === 'hard') {
      setStage('downsell');
      return;
    }
    if (isHardVariant) {
      setStage('downsell');
      return;
    }
    handleSkip();
  };

  const showContinueFree = !isHardVariant;

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xl"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
      >
        <motion.div
          animate={{ opacity: 1, scale: 1, y: 0 }}
          aria-label={t('dialogLabel')}
          aria-modal="true"
          className="relative w-full max-w-4xl overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-[#090513] via-[#120826] to-[#05020d] p-6 text-white shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          role="dialog"
          transition={{ duration: 0.3 }}
        >
          <div className="-top-32 absolute right-6 h-64 w-64 rounded-full bg-purple-500/20 blur-[140px]" />
          <div className="-bottom-24 absolute left-8 h-52 w-52 rounded-full bg-indigo-500/20 blur-[140px]" />
          <div className="relative z-10 flex flex-col gap-6">
            <div className="flex flex-col gap-3 text-center">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1 text-xs uppercase tracking-[0.4em]">
                <Sparkles className="h-4 w-4 text-amber-300" /> {t(`stages.${stage}.badge`)}
              </div>
              <h2 className="text-balance font-display text-3xl sm:text-4xl">
                {t(`contexts.${contextKey}.headline`)}
              </h2>
              <p className="text-pretty text-purple-100/90">{t(`stages.${stage}.offerLine`)}</p>
              <p className="text-pretty text-sm text-white/70">{t(`stages.${stage}.subLine`)}</p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="text-sm text-white/60 uppercase">{t('unlockTitle')}</p>
              <ul className="mt-4 space-y-3 text-base">
                {benefits.map((benefit) => (
                  <li className="flex items-start gap-3" key={benefit}>
                    <div className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/10">
                      <Check className="h-3 w-3 text-emerald-300" />
                    </div>
                    <span className="text-white/90">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-2 text-white/70 text-xs uppercase tracking-[0.3em]">
                <Shield className="h-4 w-4 text-emerald-300" />
                {t('privacy')}
              </div>
              <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-emerald-300" />
                  <p className="font-semibold text-emerald-200 text-sm">{t('guaranteeTitle')}</p>
                </div>
                <p className="mt-1 text-pretty text-emerald-200/70 text-xs">{t('guaranteeBody')}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                className="h-14 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 font-semibold text-base"
                disabled={isCreatingCheckout}
                onClick={handlePrimary}
                size="xl"
              >
                {isCreatingCheckout ? t('openingCheckout') : t(`stages.${stage}.cta`)}
              </Button>
              {showContinueFree ? (
                <button
                  className="text-center text-sm text-white/70 underline-offset-4 transition-colors hover:text-white"
                  onClick={handleSkip}
                  type="button"
                >
                  {t('continueFree')}
                </button>
              ) : null}
              <button
                className="text-center text-sm text-white/70 underline-offset-4 transition-colors hover:text-white"
                onClick={handleClose}
                type="button"
              >
                {t('smallerOption')}
              </button>
            </div>
          </div>

          <button
            aria-label={t('close')}
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/70 transition hover:text-white"
            onClick={showContinueFree ? handleSkip : handleClose}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
